'use client';

import { getClientApplicationContext } from '@hc-frontend/core-context';
import type { Integer, Placement } from '@hc-frontend/core-types';
import {
  CountMetricName,
  ProductType,
  SubProductType,
} from '@hc-frontend/core-types';
import { Tags } from '@hc-frontend/core-types';
import {
  ErrorMessage,
  FormIndicator,
  Input,
  PhoneInput,
  TextInput,
  useJourney,
  useReferrer,
} from '@hc-frontend/core-ui';
import type { PartnerConfiguration } from '@hc-frontend/features-auction';
import type { PlateName } from '@hc-frontend/features-leads';
import { useCompleteTrackingContext } from '@hc-frontend/features-tracking';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import type React from 'react';
import {
  type FormEventHandler,
  startTransition,
  useActionState,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import { useCompositionRoot } from '../../../../config/root/client';
import {
  buildPhoneValidationRefinement,
  usePhoneValidationRefinementExperiment,
} from '../../../../utils/build-phone-validation-refinement';
import { sessionReplayManager } from '../../../../utils/session-replay-manager';
import { loadConsentPartner } from '../../../actions/load-consent-partner/load-consent-partner';
import { ConsentedCTA } from './consented-cta';
import {
  FieldNames,
  leadIdUrl,
  UserInputs,
  UserInputsOptional,
} from './constants';
import { actionForm } from './form-action-server';
import { TrustedForm } from './trusted-form';

declare global {
  interface Window {
    LeadiD?: { token: string; reInit?: () => void };
    onGetLeadId: (universalId: string) => void;
    trustedFormCertUrlCallback: (certificateId: string) => void;
    myCallFunction: () => void;
  }
}

const transitions = {
  [FieldNames.LAST_NAME]: FieldNames.EMAIL,
  [FieldNames.EMAIL]: FieldNames.ADDRESS,
  [FieldNames.ADDRESS]: FieldNames.PHONE_NUMBER,
} as const;

const handleShowControls: FormEventHandler = (e) => {
  if (e.currentTarget) {
    const form = e.currentTarget as HTMLFormElement;
    form.addEventListener('input', (event) => {
      const target = event.target as HTMLInputElement;
      const key = target.name as keyof typeof transitions;
      const nextQuestion = transitions[key];
      const element = form.elements.namedItem(nextQuestion) as
        | HTMLInputElement
        | undefined;
      const container = element?.closest('.hc-form-group__stack') as
        | HTMLDivElement
        | undefined;

      if (container) {
        container.style.display = 'block';
      }
    });
  }
};

const initialQuestions: Array<string> = [
  FieldNames.FIRST_NAME,
  FieldNames.LAST_NAME,
];

export type PersonalInformationProps = {
  product: ProductType;
  subProduct?: SubProductType;
  prefilledAnswers: Partial<UserInputs>;
  ctaText: string;
  children: React.ReactNode;
  pathToSkip?: string;
  plateName: PlateName;
  placement: Placement;
  plateIndex: Integer;
  required: boolean;
  showSkipButton?: boolean;
  eHealthIds?: number[];
  SubTitle?: ({ isEhealth }: { isEhealth: boolean }) => React.JSX.Element;
};

export function PersonalInformation({
  product,
  subProduct,
  prefilledAnswers,
  ctaText,
  children,
  pathToSkip,
  placement,
  plateIndex,
  plateName,
  required,
  showSkipButton,
  eHealthIds = [],
  SubTitle,
}: PersonalInformationProps) {
  const router = useRouter();
  const [consentLoaded, setConsentLoaded] = useState(false);
  const leadIdRef = useRef('');
  const trustedFormUrlRef = useRef('');
  const { journeyId } = useJourney();
  const phoneValidationRefinement = usePhoneValidationRefinementExperiment();
  const trackingContext = useCompleteTrackingContext();

  const metricTags: Tags = {
    plate_name: plateName,
    plate_index: plateIndex,
    journey_id: journeyId,
    product,
    placement,
  };

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isEhealth, setIsEhealth] = useState(false);

  const [state, formActionHandler, isPending] = useActionState(actionForm, {
    _errors: [],
  });

  const [partnerConfiguration, setPartnerConfiguration] = useState<
    PartnerConfiguration | undefined
  >(undefined);

  const loadingConsent = useRef(false);
  const { trackSubmitPersonalInformationPlate } = useCompositionRoot();

  const { referrer } = useReferrer();

  const showPartialForm =
    product === ProductType.Enum.HEALTH ||
    (product === ProductType.Enum.MEDICARE &&
      subProduct === SubProductType.Enum.MEDICARE_SUPPLEMENT);

  const BaseUserInputs = required ? UserInputs : UserInputsOptional;

  const FinalUserInputs = BaseUserInputs.extend({
    [FieldNames.PHONE_NUMBER]: buildPhoneValidationRefinement({
      experiment: phoneValidationRefinement.experiment,
      treatment: phoneValidationRefinement.treatment,
      trackingContext,
      baseValidation: BaseUserInputs.shape[FieldNames.PHONE_NUMBER],
    }),
  });

  const {
    register,
    setFocus,
    handleSubmit,
    setError,
    formState: { errors: clientErrors, isValid },
    trigger,
    setValue,
  } = useForm<UserInputs>({
    mode: showPartialForm ? 'onTouched' : 'onSubmit',
    resolver: zodResolver(FinalUserInputs),
  });

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    Object.keys(UserInputs.shape).forEach((input) => {
      setError(input as keyof UserInputs, {
        message: state[input as keyof UserInputs]?._errors[0],
      });
    });
  }, [state, setError]);

  useEffect(() => {
    if (Object.keys(prefilledAnswers).length > 0) {
      trigger();
      Object.keys(prefilledAnswers).forEach((key) => {
        const parsedKey = key as keyof UserInputs;
        setValue(parsedKey, prefilledAnswers?.[parsedKey]);
        if (key === FieldNames.PHONE_NUMBER) {
          const element = formRef.current?.elements.namedItem(
            FieldNames.PHONE_NUMBER,
          ) as HTMLInputElement;

          if (element) {
            element.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      });
    } else if (showPartialForm && formRef.current) {
      Array.from(formRef.current.elements).forEach((element) => {
        if (!initialQuestions.includes((element as HTMLInputElement).name)) {
          const container = element.closest('.hc-form-group__stack') as
            | HTMLDivElement
            | undefined;

          if (container) {
            container.style.display = 'none';
          }
        }
      });
    }
  }, [product, prefilledAnswers, trigger, setValue, showPartialForm]);

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    handleSubmit((values) => {
      const filled = UserInputs.safeParse(values);
      // Unconditionally reporting this event, since we will always transition away from the PII page
      window.nbpix?.('event', 'submit_form');
      if (filled.success) {
        // Flush Amplitude session replay before form submission to prevent data loss
        // This is critical because the form submission triggers a redirect
        sessionReplayManager.flushSync();

        startTransition(() => {
          formActionHandler(new FormData(event.target as HTMLFormElement));
          trackSubmitPersonalInformationPlate({
            plateName,
            firstName: filled.data.firstName,
            lastName: filled.data.lastName,
            streetAddress: filled.data.address,
            phoneNumber: filled.data.phoneNumber,
            email: filled.data.email,
          });
        });
      } else if (pathToSkip && !required) {
        setIsRedirecting(true);
        router.push(pathToSkip);
      }

      window.LeadiD?.reInit?.();
    })(event);
  };

  const updateExternalIds = useCallback(
    ({
      leadIdValue,
      trustedFormUrlValue,
    }: {
      leadIdValue?: string;
      trustedFormUrlValue?: string;
    }) => {
      if (leadIdValue) {
        leadIdRef.current = leadIdValue;
      }

      if (trustedFormUrlValue) {
        trustedFormUrlRef.current = trustedFormUrlValue;
      }

      if (
        !loadingConsent.current &&
        !consentLoaded &&
        leadIdRef.current &&
        trustedFormUrlRef.current
      ) {
        loadingConsent.current = true;
        loadConsentPartner({
          productType: product,
          leadId: leadIdRef.current,
          subProductType: subProduct,
          trustedFormUrl: trustedFormUrlRef.current,
        })
          .then((response) => {
            if (response) {
              setPartnerConfiguration(response);
            }
          })
          .finally(() => {
            const { trackCountMetric } =
              getClientApplicationContext(PersonalInformation);

            trackCountMetric({
              name: CountMetricName.Enum['skip-pii'],
              tags: {
                journey_id: journeyId,
                product,
                placement,
                referrer: referrer ?? document.referrer.split('?')[0],
                skip_pii: false,
              },
            });

            setConsentLoaded(true);
            loadingConsent.current = false;
          });
      }
    },
    [
      consentLoaded,
      product,
      setPartnerConfiguration,
      setConsentLoaded,
      subProduct,
      journeyId,
      placement,
      referrer,
    ],
  );

  /** Sends a metric to Datadog for the Jornaya result, success or error. */
  const jornayaResultMetric = (jornayaResult: string) => {
    const { trackCountMetric } =
      getClientApplicationContext(PersonalInformation);

    trackCountMetric({
      name: CountMetricName.Enum['jornaya-result'],
      tags: {
        journey_id: journeyId,
        product,
        placement,
        referrer: referrer ?? document.referrer.split('?')[0],
        jornaya_result: jornayaResult,
      },
    });
  };

  useEffect(() => {
    window.trustedFormCertUrlCallback = function trustedFormCertUrlCallback(
      certUrl,
    ) {
      if (!consentLoaded) {
        updateExternalIds({ trustedFormUrlValue: certUrl });
      }
    };
  }, [consentLoaded, updateExternalIds]);

  useEffect(() => {
    window.onGetLeadId = function onGetLeadId(token: string) {
      const activeElement = document.activeElement as HTMLElement | null;
      const anotherInputIsFocused = activeElement?.matches?.(
        'input, textarea, select, [contenteditable="true"], [contenteditable=""]',
      );
      if (!consentLoaded) {
        if (!anotherInputIsFocused) {
          setFocus(FieldNames.FIRST_NAME);
        }
        updateExternalIds({ leadIdValue: token });
      }
    };
  }, [consentLoaded, updateExternalIds, setFocus]);

  useEffect(
    () => () => {
      window.LeadiD?.reInit?.();
    },
    [],
  );

  useEffect(() => {
    const partnerId = partnerConfiguration?.partnerId;
    if (
      Number.isInteger(partnerId) &&
      eHealthIds.find((id) => id === partnerId)
    ) {
      setIsEhealth(true);
    }
  }, [partnerConfiguration, eHealthIds]);

  const onlyMedicare =
    (product === 'MEDICARE' && !subProduct) || product === 'MEDICARE_ADVANTAGE';

  return (
    <>
      {partnerConfiguration?.partnerId && SubTitle ? (
        <SubTitle isEhealth={isEhealth} />
      ) : null}
      <form
        className="hc-stack-layout w-[clamp(200px,100%,500px)] justify-center"
        onInput={handleShowControls}
        ref={formRef}
        onSubmit={onSubmit}
      >
        <FormIndicator />
        <div
          className="hc-grid-layout delay-300 duration-1000"
          style={{
            '--hc-grid-layout-width-breakpoint': '220px',
          }}
        >
          <TextInput
            label="First Name"
            placeholder="First name"
            {...register(FieldNames.FIRST_NAME)}
            required={required}
            errorText={
              <ErrorMessage>
                {clientErrors[FieldNames.FIRST_NAME]?.message}
              </ErrorMessage>
            }
            error={!!clientErrors[FieldNames.FIRST_NAME]?.message}
            defaultValue={prefilledAnswers[FieldNames.FIRST_NAME]}
          />
          <TextInput
            label="Last Name"
            placeholder="Last name"
            {...register(FieldNames.LAST_NAME)}
            required={required}
            errorText={
              <ErrorMessage>
                {clientErrors[FieldNames.LAST_NAME]?.message}
              </ErrorMessage>
            }
            error={!!clientErrors[FieldNames.LAST_NAME]?.message}
            defaultValue={prefilledAnswers[FieldNames.LAST_NAME]}
          />
        </div>
        <Input
          label={onlyMedicare ? 'Email Address' : 'Email'}
          placeholder={onlyMedicare ? 'Email Address' : 'Email'}
          type="email"
          helpText={
            isEhealth ? undefined : "We'll email your results recommendation."
          }
          {...register(FieldNames.EMAIL)}
          required={required}
          errorText={
            <ErrorMessage>
              {clientErrors[FieldNames.EMAIL]?.message}
            </ErrorMessage>
          }
          error={!!clientErrors[FieldNames.EMAIL]?.message}
          defaultValue={prefilledAnswers[FieldNames.EMAIL]}
        />
        <Input
          label={`Street Address${showPartialForm ? '' : ' (optional)'}`}
          placeholder="Street Address"
          {...register(FieldNames.ADDRESS)}
          errorText={
            <ErrorMessage>
              {clientErrors[FieldNames.ADDRESS]?.message}
            </ErrorMessage>
          }
          error={!!clientErrors[FieldNames.ADDRESS]?.message}
          defaultValue={prefilledAnswers[FieldNames.ADDRESS]}
        />
        <PhoneInput
          label="Phone Number"
          placeholder="(___) ___-____"
          {...register(FieldNames.PHONE_NUMBER)}
          required={required}
          errorText={
            <ErrorMessage>
              {clientErrors[FieldNames.PHONE_NUMBER]?.message}
            </ErrorMessage>
          }
          error={!!clientErrors[FieldNames.PHONE_NUMBER]?.message}
          defaultValue={prefilledAnswers[FieldNames.PHONE_NUMBER]}
        />
        <div className="text-center">
          <ErrorMessage>{state._errors}</ErrorMessage>
        </div>

        {partnerConfiguration?.tcpaConsent && (
          <ConsentedCTA
            tcpaConsent={partnerConfiguration.tcpaConsent}
            showPartialForm={showPartialForm}
            product={product}
            subProduct={subProduct}
            ctaText={isEhealth ? 'Get My Results Now' : ctaText}
            formRef={formRef}
            isValid={isValid}
            pathToSkip={pathToSkip}
            metricTags={metricTags}
            isPending={isPending || isRedirecting}
            showSkipButton={showSkipButton}
          />
        )}

        {children}

        <Script
          src={`${leadIdUrl}/campaign/d41d6781-7d2b-3af5-e3af-d0ebfe85d74f.js?snippet_version=2&callback=onGetLeadId`}
          id="LeadiDscript_campaign"
          data-testid="lead-id"
          onReady={() => {
            jornayaResultMetric('success');
            if (!leadIdRef.current && window.LeadiD?.token) {
              window.LeadiD?.reInit?.();
              updateExternalIds({ leadIdValue: window.LeadiD.token });
            }
          }}
          onError={() => {
            jornayaResultMetric('error');
          }}
        />

        <TrustedForm trustedFormValue={trustedFormUrlRef.current} />
      </form>
    </>
  );
}
